/* Divine Medicinals — Wish List
   ------------------------------------------------------------------
   Multi-folder wish lists stored in the shopper's browser (localStorage).
   No app, no account required. Works for guests and signed-in customers.

   Storage shape (key: dm-wishlist-v1)
     { v:1, active:"<listId>", lists:[ { id, name, created, items:[ item ] } ] }
   item
     { h:handle, vid:variantId, q:qty, added:ts,
       s:{ t:title, vt:variantTitle, p:priceCents, cp:compareAtCents, img:url, u:productUrl } }
   The `s` snapshot lets a list paint instantly; live price/stock is then
   refreshed from /products/<handle>.js so nothing goes stale.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var KEY = 'dm-wishlist-v1';
  var CFG = window.DMWishlistConfig || {};
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var MONEY = CFG.moneyFormat || '${{amount}}';
  var PAGE = CFG.pageUrl || '/pages/wish-list';
  var DEFAULT_NAME = CFG.defaultListName || 'My Wish List';

  /* ---------------- storage ----------------

     Document (v2). Deletions leave tombstones so that merging two devices does not
     resurrect something the shopper removed on the other one.

       { v:2, u:<ts>, active:<listId>,
         lists:[ { id, name, created, u, items:[ {h, vid, q, added, u, s} ] } ],
         tomb:{ l:{ "<listId>": ts }, i:{ "<listId>|<h>|<vid>": ts } } }

     `u` is a last-touched stamp; merges are last-writer-wins per list and per item.
     `s` is a display snapshot and is stripped before the document is sent to the
     server — the wish list page re-fetches live product data anyway.
     ------------------------------------------------------------------------- */

  var GUEST_KEY = 'dm-wishlist-v1';
  var TOMB_TTL = 90 * 24 * 60 * 60 * 1000; // forget deletions after 90 days

  var customerId = null;   // set by the sync layer once the server tells us who this is
  var state = null;

  function bucketKey() {
    return customerId ? 'dm-wishlist-c-' + customerId : GUEST_KEY;
  }

  function now() { return Date.now(); }

  function uid(prefix) {
    return (prefix || 'l') + '_' + now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  function tombKey(listId, handle, vid) {
    return listId + '|' + handle + '|' + (vid === null || vid === undefined ? '' : vid);
  }

  function blank() {
    var id = 'l_default', t = now();
    return {
      v: 2, u: t, active: id,
      lists: [{ id: id, name: DEFAULT_NAME, created: t, u: t, items: [] }],
      tomb: { l: {}, i: {} }
    };
  }

  /** Accept a v1 document, anything half-written, or junk, and return a sane v2 doc. */
  function normalise(d) {
    if (!d || typeof d !== 'object' || !Array.isArray(d.lists)) return blank();
    var t = now();
    var out = {
      v: 2,
      u: typeof d.u === 'number' ? d.u : t,
      active: d.active || null,
      lists: [],
      tomb: { l: {}, i: {} }
    };
    if (d.tomb && typeof d.tomb === 'object') {
      ['l', 'i'].forEach(function (k) {
        var src = d.tomb[k];
        if (src && typeof src === 'object') {
          Object.keys(src).forEach(function (key) {
            var ts = +src[key];
            if (ts > 0) out.tomb[k][key] = ts;
          });
        }
      });
    }
    d.lists.forEach(function (l) {
      if (!l || typeof l !== 'object') return;
      var list = {
        id: l.id || uid(),
        name: (typeof l.name === 'string' && l.name.trim()) ? l.name.slice(0, 60) : DEFAULT_NAME,
        created: +l.created || t,
        u: +l.u || +l.created || t,
        items: []
      };
      (Array.isArray(l.items) ? l.items : []).forEach(function (it) {
        if (!it || !it.h) return;
        var added = +it.added || t;
        list.items.push({
          h: String(it.h),
          vid: (it.vid === null || it.vid === undefined || it.vid === '') ? null : String(it.vid),
          q: Math.max(1, parseInt(it.q, 10) || 1),
          added: added,
          u: +it.u || added,
          s: (it.s && typeof it.s === 'object') ? it.s : {}
        });
      });
      out.lists.push(list);
    });
    if (!out.lists.length) return blank();
    if (!getList(out, out.active)) out.active = out.lists[0].id;
    return prune(out);
  }

  /** Drop tombstones old enough that no device could still be carrying the item. */
  function prune(d) {
    var cutoff = now() - TOMB_TTL;
    ['l', 'i'].forEach(function (k) {
      Object.keys(d.tomb[k]).forEach(function (key) {
        if (d.tomb[k][key] < cutoff) delete d.tomb[k][key];
      });
    });
    return d;
  }

  function read() {
    if (state) return state;
    try {
      var raw = localStorage.getItem(bucketKey());
      state = raw ? normalise(JSON.parse(raw)) : blank();
    } catch (e) {
      state = blank();
    }
    return state;
  }

  /** Persist and announce. origin:'sync' means the change came from the server, so the
      sync layer should not turn round and push it straight back. */
  function save(origin) {
    if (!state) return;
    state.u = now();
    try { localStorage.setItem(bucketKey(), JSON.stringify(state)); } catch (e) { /* private mode / quota */ }
    emit(origin || 'local');
  }

  function emit(origin) {
    document.dispatchEvent(new CustomEvent('dm:wishlist:change', {
      detail: { state: state, origin: origin || 'local' }
    }));
  }

  // keep other open tabs in step
  window.addEventListener('storage', function (e) {
    if (e.key !== bucketKey()) return;
    state = null; read(); emit('storage');
  });

  /* ---------------- merging two devices ---------------- */

  function cloneList(l) {
    return {
      id: l.id, name: l.name, created: l.created, u: l.u,
      items: l.items.map(function (it) {
        return { h: it.h, vid: it.vid, q: it.q, added: it.added, u: it.u, s: it.s || {} };
      })
    };
  }

  function upsertItem(list, it) {
    var i = findIn(list, it.h, it.vid);
    if (i < 0) { list.items.push({ h: it.h, vid: it.vid, q: it.q, added: it.added, u: it.u, s: it.s || {} }); return; }
    var cur = list.items[i];
    if ((it.u || 0) > (cur.u || 0)) {
      cur.q = it.q; cur.u = it.u;
      cur.added = Math.min(cur.added || it.added, it.added || cur.added);
    }
    // a snapshot from either side beats no snapshot at all
    if ((!cur.s || !cur.s.t) && it.s && it.s.t) cur.s = it.s;
  }

  /**
   * Last-writer-wins union of two documents. A list or item stays deleted while its
   * tombstone is newer than the surviving copy's last edit; edit it again on another
   * device and that later edit wins, which is what a shopper expects.
   */
  function mergeDocs(a, b) {
    a = normalise(a); b = normalise(b);
    var out = { v: 2, u: Math.max(a.u || 0, b.u || 0), active: null, lists: [], tomb: { l: {}, i: {} } };

    [a, b].forEach(function (d) {
      ['l', 'i'].forEach(function (k) {
        Object.keys(d.tomb[k]).forEach(function (key) {
          out.tomb[k][key] = Math.max(out.tomb[k][key] || 0, d.tomb[k][key]);
        });
      });
    });

    var byId = {};
    [a, b].forEach(function (d) {
      d.lists.forEach(function (l) {
        var prev = byId[l.id];
        if (!prev) { byId[l.id] = cloneList(l); return; }
        if ((l.u || 0) > (prev.u || 0)) { prev.name = l.name; prev.u = l.u; }
        prev.created = Math.min(prev.created, l.created);
        l.items.forEach(function (it) { upsertItem(prev, it); });
      });
    });

    Object.keys(byId).forEach(function (id) {
      var l = byId[id];
      if ((out.tomb.l[id] || 0) >= (l.u || l.created || 0)) { delete byId[id]; return; }
      l.items = l.items.filter(function (it) {
        return (it.u || it.added || 0) > (out.tomb.i[tombKey(id, it.h, it.vid)] || 0);
      });
      l.items.sort(function (x, y) { return (y.added || 0) - (x.added || 0); });
    });

    out.lists = Object.keys(byId).map(function (k) { return byId[k]; })
      .sort(function (x, y) { return (x.created || 0) - (y.created || 0); });

    if (!out.lists.length) return blank();
    var newer = (a.u || 0) >= (b.u || 0) ? a : b;
    out.active = getList(out, newer.active) ? newer.active : out.lists[0].id;
    return prune(out);
  }

  /** Replace the working document wholesale (used by the sync layer after a merge). */
  function adopt(doc, origin) {
    state = normalise(doc);
    try { localStorage.setItem(bucketKey(), JSON.stringify(state)); } catch (e) {}
    emit(origin || 'sync');
    return state;
  }

  /** Compact form for the server: no display snapshots, no empty tombstone maps. */
  function forServer(d) {
    d = d || read();
    return {
      v: 2, u: d.u, active: d.active,
      lists: d.lists.map(function (l) {
        return {
          id: l.id, name: l.name, created: l.created, u: l.u,
          items: l.items.map(function (it) {
            return { h: it.h, vid: it.vid, q: it.q, added: it.added, u: it.u };
          })
        };
      }),
      tomb: d.tomb
    };
  }

  /** Point storage at a customer's bucket (or back at the guest bucket with null). */
  function setCustomer(id) {
    id = id ? String(id) : null;
    if (id === customerId) return read();
    customerId = id;
    state = null;
    return read();
  }

  function getCustomer() { return customerId; }

  /** Fold a guest document into the current (customer) one and clear the guest bucket. */
  function absorbGuest() {
    var raw;
    try { raw = localStorage.getItem(GUEST_KEY); } catch (e) { return false; }
    if (!raw) return false;
    var guest;
    try { guest = normalise(JSON.parse(raw)); } catch (e) { guest = null; }
    var hasItems = guest && guest.lists.some(function (l) { return l.items.length; });
    try { localStorage.removeItem(GUEST_KEY); } catch (e) {}
    if (!hasItems) return false;
    adopt(mergeDocs(read(), guest), 'local');
    return true;
  }

  /* ---------------- model helpers ---------------- */

  function getList(d, id) {
    d = d || state;
    if (!d || !d.lists) return null;
    for (var i = 0; i < d.lists.length; i++) if (d.lists[i].id === id) return d.lists[i];
    return null;
  }

  function activeList() {
    var d = read();
    return getList(d, d.active) || d.lists[0];
  }

  function sameItem(it, handle, vid) {
    return it.h === handle && String(it.vid === null || it.vid === undefined ? '' : it.vid) === String(vid === null || vid === undefined ? '' : vid);
  }

  function findIn(list, handle, vid) {
    for (var i = 0; i < list.items.length; i++) if (sameItem(list.items[i], handle, vid)) return i;
    return -1;
  }

  /** Every list id that currently holds this product/variant. */
  function listsHolding(handle, vid) {
    var d = read(), out = [];
    d.lists.forEach(function (l) { if (findIn(l, handle, vid) > -1) out.push(l.id); });
    return out;
  }

  /** Unique product+variant pairs across every list — what the header badge counts. */
  function totalCount() {
    var d = read(), seen = {};
    d.lists.forEach(function (l) {
      l.items.forEach(function (it) { seen[it.h + '::' + it.vid] = 1; });
    });
    return Object.keys(seen).length;
  }

  function addTo(listId, item) {
    var l = getList(read(), listId);
    if (!l) return false;
    if (findIn(l, item.h, item.vid) > -1) return false;
    var t = now();
    var vid = (item.vid === null || item.vid === undefined || item.vid === '') ? null : String(item.vid);
    l.items.unshift({
      h: item.h,
      vid: vid,
      q: Math.max(1, parseInt(item.q, 10) || 1),
      added: t,
      u: t,
      s: item.s || {}
    });
    l.u = t;
    delete state.tomb.i[tombKey(listId, item.h, vid)];
    save();
    return true;
  }

  function removeFrom(listId, handle, vid) {
    var l = getList(read(), listId);
    if (!l) return false;
    var i = findIn(l, handle, vid);
    if (i < 0) return false;
    var stored = l.items[i].vid;
    l.items.splice(i, 1);
    l.u = now();
    state.tomb.i[tombKey(listId, handle, stored)] = l.u;
    save();
    return true;
  }

  function removeEverywhere(handle, vid) {
    var d = read(), hit = false, t = now();
    d.lists.forEach(function (l) {
      var i = findIn(l, handle, vid);
      if (i > -1) {
        var stored = l.items[i].vid;
        l.items.splice(i, 1);
        l.u = t;
        d.tomb.i[tombKey(l.id, handle, stored)] = t;
        hit = true;
      }
    });
    if (hit) save();
    return hit;
  }

  function setQty(listId, handle, vid, q) {
    var l = getList(read(), listId);
    if (!l) return;
    var i = findIn(l, handle, vid);
    if (i < 0) return;
    l.items[i].q = Math.max(1, parseInt(q, 10) || 1);
    l.items[i].u = now();
    l.u = l.items[i].u;
    save();
  }

  function moveItem(fromId, toId, handle, vid) {
    if (fromId === toId) return;
    var d = read(), from = getList(d, fromId), to = getList(d, toId);
    if (!from || !to) return;
    var i = findIn(from, handle, vid);
    if (i < 0) return;
    var it = from.items[i], t = now();
    from.items.splice(i, 1);
    from.u = t;
    d.tomb.i[tombKey(fromId, handle, it.vid)] = t;
    if (findIn(to, handle, vid) < 0) {
      it.u = t;
      to.items.unshift(it);
      delete d.tomb.i[tombKey(toId, handle, it.vid)];
    }
    to.u = t;
    save();
  }

  function createList(name) {
    var d = read(), t = now();
    name = (name || '').trim() || 'New list';
    var existing = d.lists.filter(function (l) { return l.name.toLowerCase() === name.toLowerCase(); })[0];
    if (existing) return existing.id;
    var l = { id: uid(), name: name.slice(0, 60), created: t, u: t, items: [] };
    d.lists.push(l);
    delete d.tomb.l[l.id];
    save();
    return l.id;
  }

  function renameList(id, name) {
    var l = getList(read(), id);
    if (!l) return;
    name = (name || '').trim();
    if (!name) return;
    l.name = name.slice(0, 60);
    l.u = now();
    save();
  }

  function deleteList(id) {
    var d = read();
    if (d.lists.length < 2) return false; // never leave the shopper with nothing
    d.lists = d.lists.filter(function (l) { return l.id !== id; });
    d.tomb.l[id] = now();
    if (d.active === id) d.active = d.lists[0].id;
    save();
    return true;
  }

  function setActive(id) {
    var d = read();
    if (!getList(d, id)) return;
    d.active = id;
    save();
  }

  /* ---------------- money ---------------- */

  function pad(n, w) { n = String(n); while (n.length < w) n = '0' + n; return n; }

  function group(num, sep) {
    return num.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + sep);
  }

  /** Shopify's money_format renderer — handles the four placeholder styles. */
  function money(cents, format) {
    if (cents === null || cents === undefined || isNaN(cents)) return '';
    var fmt = (format || MONEY).replace(/<[^>]*>/g, '');
    var value = '';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = m ? m[1] : 'amount';
    var amount = Number(cents) / 100;

    function withDelims(dec, thousand, decimal) {
      var parts = Math.abs(amount).toFixed(dec).split('.');
      var whole = group(parts[0], thousand);
      return dec > 0 && parts[1] ? whole + decimal + parts[1] : whole;
    }

    switch (token) {
      case 'amount': value = withDelims(2, ',', '.'); break;
      case 'amount_no_decimals': value = withDelims(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelims(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(0, ' ', ','); break;
      case 'amount_with_space_separator': value = withDelims(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelims(2, ' ', '.'); break;
      default: value = withDelims(2, ',', '.');
    }
    var out = fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    return (amount < 0 ? '-' : '') + out;
  }

  /* ---------------- storefront calls ---------------- */

  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    var p = fetch(ROOT.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('not found');
      return r.json();
    }).catch(function () { return null; });
    productCache[handle] = p;
    return p;
  }

  // lets the free-shipping meter re-read /cart.js without a page reload
  function cartChanged() {
    document.dispatchEvent(new CustomEvent('dm:cart:updated'));
  }

  function addToCart(items) {
    return fetch(ROOT.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) throw new Error((body && body.description) || 'Could not add to cart');
        cartChanged();
        return body;
      });
    });
  }

  function changeCartLine(line, quantity) {
    return fetch(ROOT.replace(/\/$/, '') + '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) {
      cartChanged();
      return r.json();
    });
  }

  /* ---------------- share links ---------------- */

  function b64url(str) {
    return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function unb64url(str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) str += '=';
    return decodeURIComponent(escape(atob(str)));
  }

  function shareUrl(list) {
    var payload = {
      n: list.name,
      i: list.items.map(function (it) { return [it.h, it.vid, it.q || 1]; })
    };
    return location.origin + PAGE + '#shared=' + b64url(JSON.stringify(payload));
  }

  function readShared() {
    var m = (location.hash || '').match(/shared=([A-Za-z0-9\-_]+)/);
    if (!m) return null;
    try {
      var d = JSON.parse(unb64url(m[1]));
      if (!d || !Array.isArray(d.i)) return null;
      return { name: d.n || 'Shared list', items: d.i };
    } catch (e) { return null; }
  }

  /* ---------------- toast ---------------- */

  var toastEl = null, toastTimer = null;

  function toast(message, actionLabel, actionFn) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var msg = document.createElement('span');
    msg.className = 'wl-toast__msg';
    msg.textContent = message;
    toastEl.appendChild(msg);
    if (actionLabel && actionFn) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wl-toast__act';
      btn.textContent = actionLabel;
      btn.addEventListener('click', function () { hideToast(); actionFn(); });
      toastEl.appendChild(btn);
    }
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, actionLabel ? 6000 : 3200);
  }

  function hideToast() { if (toastEl) toastEl.classList.remove('is-on'); }

  /* ---------------- picker modal ---------------- */

  var modal = null, modalPrev = null;

  function buildModal() {
    modal = document.createElement('div');
    modal.className = 'wl-modal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="wl-modal__scrim" data-wl-close></div>' +
      '<div class="wl-modal__box" role="dialog" aria-modal="true" aria-labelledby="wlModalTitle">' +
        '<div class="wl-modal__head">' +
          '<h2 id="wlModalTitle" class="wl-modal__title">Save to a list</h2>' +
          '<button type="button" class="wl-modal__x" data-wl-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<p class="wl-modal__sub" data-wl-prod></p>' +
        '<div class="wl-modal__lists" data-wl-lists></div>' +
        '<form class="wl-modal__new" data-wl-newform>' +
          '<input type="text" data-wl-newname maxlength="60" placeholder="New list name (e.g. Items for my wife)" aria-label="New list name">' +
          '<button type="submit" class="btn btn--ghost wl-btn-sm">Create</button>' +
        '</form>' +
        '<div class="wl-modal__foot">' +
          '<a class="wl-modal__link" data-wl-goto>View my lists</a>' +
          '<button type="button" class="btn btn--primary wl-btn-sm" data-wl-close>Done</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-wl-close]')) closeModal();
    });
    modal.querySelector('[data-wl-newform]').addEventListener('submit', function (e) {
      e.preventDefault();
      var input = modal.querySelector('[data-wl-newname]');
      var name = input.value.trim();
      if (!name) return;
      var id = createList(name);
      input.value = '';
      if (modal.__item) { addTo(id, modal.__item); toast('Saved to “' + name + '”'); }
      renderModalLists();
    });
    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key === 'Tab') trapFocus(e);
    });
  }

  function trapFocus(e) {
    var f = modal.querySelectorAll('button, input, a[href], [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function renderModalLists() {
    var wrap = modal.querySelector('[data-wl-lists]');
    var item = modal.__item;
    var d = read();
    wrap.innerHTML = '';
    d.lists.forEach(function (l) {
      var on = item ? findIn(l, item.h, item.vid) > -1 : false;
      var row = document.createElement('label');
      row.className = 'wl-pick' + (on ? ' is-on' : '');
      row.innerHTML =
        '<input type="checkbox"' + (on ? ' checked' : '') + '>' +
        '<span class="wl-pick__name"></span>' +
        '<span class="wl-pick__n">' + l.items.length + '</span>';
      row.querySelector('.wl-pick__name').textContent = l.name;
      row.querySelector('input').addEventListener('change', function () {
        if (!item) return;
        if (this.checked) { addTo(l.id, item); setActive(l.id); }
        else { removeFrom(l.id, item.h, item.vid); }
        renderModalLists();
      });
      wrap.appendChild(row);
    });
  }

  function openPicker(item) {
    if (!modal) buildModal();
    modal.__item = item;
    modalPrev = document.activeElement;
    var sub = modal.querySelector('[data-wl-prod]');
    sub.textContent = item && item.s && item.s.t ? item.s.t : '';
    sub.hidden = !sub.textContent;
    modal.querySelector('[data-wl-goto]').setAttribute('href', PAGE);
    renderModalLists();
    modal.hidden = false;
    document.body.classList.add('wl-locked');
    var first = modal.querySelector('.wl-pick input, [data-wl-newname]');
    if (first) first.focus();
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('wl-locked');
    if (modalPrev && modalPrev.focus) modalPrev.focus();
  }

  /* ---------------- buttons on cards / PDP ---------------- */

  /** Read a wish-list item out of a button's data attributes. */
  function itemFromEl(el) {
    var vid = el.getAttribute('data-wl-vid') || '';
    // a PDP with variant radios should follow whatever is selected right now
    var form = el.closest('form');
    if (form) {
      var picked = form.querySelector('input[name="id"]:checked, select[name="id"]');
      if (picked && picked.value) vid = picked.value;
      var hidden = form.querySelector('input[name="id"][type="hidden"]');
      if (!picked && hidden && hidden.value) vid = hidden.value;
    }
    // start from the qty the markup declared (a cart line carries its own),
    // then let a product form's quantity field win if there is one
    var qty = Math.max(1, parseInt(el.getAttribute('data-wl-qty'), 10) || 1);
    var qtyInput = form && form.querySelector('input[name="quantity"]');
    if (qtyInput) qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
    return {
      h: el.getAttribute('data-wl-handle'),
      vid: vid,
      q: qty,
      s: {
        t: el.getAttribute('data-wl-title') || '',
        vt: el.getAttribute('data-wl-vtitle') || '',
        p: parseInt(el.getAttribute('data-wl-price'), 10),
        cp: parseInt(el.getAttribute('data-wl-compare'), 10) || null,
        img: el.getAttribute('data-wl-img') || '',
        u: el.getAttribute('data-wl-url') || ''
      }
    };
  }

  function syncButtons() {
    var d = read();
    document.querySelectorAll('[data-wl-toggle]').forEach(function (btn) {
      var item = itemFromEl(btn);
      var on = listsHolding(item.h, item.vid).length > 0;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = btn.querySelector('[data-wl-label]');
      if (label) label.textContent = on ? (btn.getAttribute('data-wl-label-on') || 'Saved') : (btn.getAttribute('data-wl-label-off') || 'Save to Wish List');
      if (!btn.querySelector('[data-wl-label]')) {
        btn.setAttribute('aria-label', (on ? 'Saved to wish list: ' : 'Save to wish list: ') + (item.s.t || 'product'));
        btn.setAttribute('title', on ? 'In your wish list' : 'Save to wish list');
      }
    });
    document.querySelectorAll('[data-wl-count]').forEach(function (el) {
      var n = totalCount();
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
    });
  }

  function onToggleClick(btn) {
    var item = itemFromEl(btn);
    if (!item.h) return;
    var holders = listsHolding(item.h, item.vid);
    var d = read();

    if (holders.length) {
      // already saved — one click takes it back out of every list
      removeEverywhere(item.h, item.vid);
      toast('Removed from your wish list', 'Undo', function () {
        holders.forEach(function (id) { addTo(id, item); });
        toast('Put back');
      });
      return;
    }

    if (d.lists.length > 1) {
      // more than one folder exists, so let them choose where it goes
      openPicker(item);
      return;
    }

    var target = activeList();
    addTo(target.id, item);
    toast('Saved to “' + target.name + '”', 'Choose a list', function () { openPicker(item); });
  }

  /* ---------------- cart page: save for later ---------------- */

  function saveLineForLater(btn) {
    var item = itemFromEl(btn);
    var line = parseInt(btn.getAttribute('data-wl-save-line'), 10);
    if (!item.h || !line) return;
    btn.disabled = true;
    var target = activeList();
    addTo(target.id, item);
    changeCartLine(line, 0).then(function () {
      location.reload();
    }).catch(function () {
      btn.disabled = false;
      toast('Saved to “' + target.name + '”, but the cart could not be updated.');
    });
  }

  /* ---------------- global click routing ---------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;

    var toggle = t.closest && t.closest('[data-wl-toggle]');
    if (toggle) {
      e.preventDefault();
      e.stopPropagation(); // product cards are wrapped in a link
      onToggleClick(toggle);
      return;
    }

    var later = t.closest && t.closest('[data-wl-save-line]');
    if (later) { e.preventDefault(); saveLineForLater(later); return; }

    var open = t.closest && t.closest('[data-wl-open-picker]');
    if (open) {
      e.preventDefault();
      var host = open.closest('[data-wl-handle]') || open;
      openPicker(itemFromEl(host));
    }
  });

  document.addEventListener('dm:wishlist:change', syncButtons);
  document.addEventListener('change', function (e) {
    // PDP variant switch: the heart tracks the selected variant
    if (e.target.name === 'id') syncButtons();
  });

  /* ---------------- public API ---------------- */

  window.DMWish = {
    read: read, save: save, getList: getList, activeList: activeList, setActive: setActive,
    // account sync surface (used by wishlist-sync.js)
    mergeDocs: mergeDocs, adopt: adopt, forServer: forServer,
    setCustomer: setCustomer, getCustomer: getCustomer, absorbGuest: absorbGuest,
    listsHolding: listsHolding, totalCount: totalCount, findIn: findIn,
    addTo: addTo, removeFrom: removeFrom, removeEverywhere: removeEverywhere,
    setQty: setQty, moveItem: moveItem,
    createList: createList, renameList: renameList, deleteList: deleteList,
    money: money, fetchProduct: fetchProduct, addToCart: addToCart,
    openPicker: openPicker, toast: toast, sync: syncButtons,
    shareUrl: shareUrl, readShared: readShared,
    uid: uid, pageUrl: PAGE
  };

  if (document.readyState !== 'loading') syncButtons();
  else document.addEventListener('DOMContentLoaded', syncButtons);
})();
